import React, { ReactElement } from 'react';
import {
  Component,
  FilterCombo,
  Position,
  noDataDropDownRender
} from "puzzle";
import { Skeleton } from '@progress/kendo-react-indicators';
import { Field } from "../../../../UI/Field/Field";
import {
  ComboBoxChangeEvent,
  ComboBoxFilterChangeEvent,
  ListItemProps
} from "@progress/kendo-react-dropdowns";
import _, { get } from "lodash";
import virtualScroll from "../../../../VirtualScroll";
import {
  DEFAULT_PROPS,
  GET_USERS_REQUEST_DATA,
  NEW_STATE,
  PROPS,
  STATE,
  USER,
  USER_EXTENDED
} from "./Types";

export class AssignFilesSupplierDialog extends Component <STATE, PROPS> {
  initialPaginationData = {
    data: [],
    pageDataPopulated: [],
    pageNumber: 0,
    skip: 0,
    total: 0
  };

  gridRef: any;

  static defaultProps: DEFAULT_PROPS = {
    pageSize: 25,
    requestActionName: 'getUsersForFileAssign'
  };

  /**
   * @inheritDoc
   */
  constructor(props: PROPS) {
    super(props);
    const { assignedUser } = this.props.summaryInfo;
    let value = undefined;
    let allowCustom = false;
    if (assignedUser) {
      value = {
        fullName: assignedUser === 'MULTIPLE' ? this.translate(assignedUser) : assignedUser,
        userId: -1
      };
      allowCustom = true;
    }

    this.state = {
      ...this.state,
      ...this.initialPaginationData,
      filter: '',
      requestInProgress: false,
      searchInProgress: false,
      value,
      allowCustom
    } as STATE;
  };

  /**
   * On close handler
   */
  onClose = () => {
    this.setState({
      filter: ''
    });
  };

  /**
   * Filter Change handler (Is being debounced)
   * @param event
   */
  filterChange = (event: ComboBoxFilterChangeEvent): void => {
    this.setState({
      ...this.initialPaginationData,
      filter: event.filter.value,
      searchInProgress: true
    }, () => {
      this.getInitialDropdownData();
      this.updateFooter();
    });
  };

  /**
   * updates filtering dispatch action.
   */
  getInitialDropdownData = (): void => {
    this.dispatch(this.props.requestActionName, Position.end, this.prepareRequestParams(0));
  };

  /**
   * Gets userId value of selected supplier
   */
  getDataForAssign = () => {
    const {
      assignRequestParams,
      taskId,
      submissionId,
    } = this.props.dialogData;

    return {
      dialogData: {
        url: `/jobPhaseFiles/${taskId}/assign`,
        hasCompletedFiles: this.props.summaryInfo.completedTargetsCount > 0,
        params: {
          ...assignRequestParams,
          submissionId,
          assigneeId: get(this.state.value, 'userId'),
        }
      }
    }
  };


  /**
   * Render custom element for dropdown menu item
   * @param li
   * @param itemProps
   */
  itemRender = (li: ReactElement<HTMLLIElement>, itemProps: ListItemProps): ReactElement => {
    const { fullName = '' } = itemProps.dataItem;

    let render = fullName ? React.cloneElement(
      li,
      {
        ...li.props,
        id: itemProps.id?.substr(7, itemProps.id?.length)
      },
      fullName
    ) : <Skeleton
      shape={'text'}
      style={{ width: '100%' }}
      animation={{ type: 'wave' }}
    />;

    return render;
  };

  /**
   * Handles filterCombo value change
   * @param event
   */
  onDropdownChange = (event: ComboBoxChangeEvent): void => {
    this.setState({ value: event.value }, this.updateFooter);
  };

  /**
   * On open handler
   */
  onOpen = () => {
    const {
      data,
      filter,
      value
    } = this.state;

    setTimeout(() => {
      if ((!value || value.userId === -1) && !data.length && !filter) {
        let stateData: NEW_STATE = {
          searchInProgress: true,
          allowCustom: false,
          value: {
            fullName: '',
            userId: -1
          }
        };
        this.setState({ ...stateData }, this.getInitialDropdownData)
      }
    }, 0);
  };

  /**
   * If data still isn't loaded sets displays values.
   * @param data
   */
  prepareDataForDisplay = (data: USER_EXTENDED[]): USER_EXTENDED[] => {
    let mappedData = data.map((item) => {
      if (!get(item, 'userId')) {
        item = {
          ...item,
          firstName: '',
          fullName: '',
          lastName: '',
          userId: -1
        }
      }
      return item;
    });
    return mappedData;
  };

  /**
   * Prepare params for combo action request
   * @param pageNumber
   */
  prepareRequestParams = (pageNumber: number): { url: string } => {
    const {
      dialogData,
      pageSize
    } = this.props;
    const {
      projectId,
      sourceLanguage,
      supplierId,
      targetLanguage
    } = dialogData;
    const { filter: fullName } = this.state;

    let url: string = `/projects/${projectId}/users/search?pageSize=${pageSize}&pageNumber=${pageNumber}&fullName=${fullName}&userType=vendor`;
    let additionalParams = `&supplierId=${supplierId}&targetLanguageLocal=${targetLanguage.locale}&sourceLanguageLocal=${sourceLanguage.locale}`;
    url += additionalParams;

    return { url };
  };

  /**
   * Sets response data to virtualScrolling
   * @param data
   */
  setGridData = (data: GET_USERS_REQUEST_DATA): void => {
    let { users = [], gridContentInfo } = data;
    users = users.map((item: USER) => {
      const { firstName, lastName } = item;
      return {
        ...item,
        fullName: `${firstName} ${lastName}`
      } as USER_EXTENDED
    });

    const repackedData = {
      gridContentInfo,
      items: users
    };

    this.setState({
      searchInProgress: false
    }, this.props.virtualScroll.setGridData(repackedData))
  };

  /**
   * Performs footer update on current dialog
   */
  updateFooter = () => {
    this.dispatch('updateFileAssignFooterButtons', '', { disabled: !_.get(this.state.value, 'userId') });
  };

  renderBody(): JSX.Element {
    const {
      allowCustom,
      data,
      searchInProgress,
      skip,
      total,
      value
    } = this.state;
    const {
      pageSize,
      virtualScroll
    } = this.props;

    let displayData = data.slice(skip, skip + pageSize);
    displayData = this.prepareDataForDisplay(displayData);
    const noDataTranslationKey = searchInProgress ? 'searching' : 'noData';

    return (
      <Field>
        <FilterCombo
          allowCustom={allowCustom}
          data={displayData}
          filterChange={_.debounce(this.filterChange, 300)}
          onOpen={this.onOpen}
          onClose={this.onClose}
          itemRender={this.itemRender}
          label={this.translate('user')}
          listNoDataRender={() => noDataDropDownRender(this.translate(noDataTranslationKey))}
          name='assignFiles'
          onChange={this.onDropdownChange}
          onPageChange={virtualScroll?.pageChange}
          popupSettings={{ className: 'pd-assign-dropdown' }}
          ref={(el) => this.gridRef = el}
          textField='fullName'
          translateMethod={this.translate.bind(this)}
          value={value}
          virtual={{
            pageSize: pageSize,
            skip: skip,
            total: total
          }}
        />
      </Field>
    );
  }
}

export default virtualScroll(AssignFilesSupplierDialog);
